import * as React from 'react';
import { connect } from 'react-redux';

import CampaignOutfitsHeader from '../campaign-outfits-header/CampaignOutfitsHeader';
import ActionLink from 'components/buttons/action-link/ActionLink';
import CreatorInfoWithChannels from 'components/creator-info-with-channels/CreatorInfoWithChannels';
import PageHeading from 'components/page/page-heading/PageHeading';
import PageSection from 'components/page/page-section/PageSection';
import SortableTable from 'components/sortable-table/SortableTable';
import Spinner from 'components/spinner/Spinner';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';

import { createCampaignOutfits, fetchCampaignOutfits, resetCampaignOutfits } from '../../reducers/campaign-outfits/campaign-outfits';
import { success } from 'redux-store/reducers/common-reducer';
import { fetchInfluncerMetrics } from 'modules/influencers/reducers/influencersMetrics';

import { getDisplayDate, isToday } from 'utils/date';
import { isInfluencerProfileRejected } from 'utils/profile';
import { copyTextToClipboard } from 'utils/helper/common';
import { __t, applyTranslations } from 'utils/language';

import type { CampaignOutfit, InfluencerMetrics, SortableTableColumnType } from 'types';

type OwnProps = {
  campaignId: number
};

type DispatchProps = {
  dispatchFetchCampaignOutfits: (a: number) => void,
  dispatchResetCampaignOutfits: () => void,
  dispatchCreateCampaignOutfits: (b: number, a: Array<number>) => void,
  dispatchSuccessMessage: (a: string) => void,
  dispatchFetchInfluncerMetrics: (campaignId: number) => void
};

type StateProps = {
  campaignOutfitsItems: Array<CampaignOutfit>,
  campaignOutfitsLoading: boolean,
  influencersMetrics: InfluencerMetrics
};

type Props = OwnProps & StateProps & DispatchProps;

import './campaign-outfits.scss';

applyTranslations(require('./campaign-outfits.i18n.json'));

const getCopyOutfitLinkSelector = (subcampaignId: number): string => `js-outfitUrl-${subcampaignId}`;

const getColumns = (metrics): Array<SortableTableColumnType> => [
  {
    title: __t('campaign_outfits.column.creators'),
    key: 'creators',
    width: 352,
    sort: (a: CampaignOutfit, b: CampaignOutfit) => a.creatorProfile.name?.localeCompare(b.creatorProfile.name),
    render: (col: CampaignOutfit) => (
      <div className="campaign-outfits__creator">
        <CreatorInfoWithChannels
          creatorProfile={col.creatorProfile}
          metrics={metrics[col.creatorProfile.id]}
          includeRatio
        />
      </div>
    )
  },
  {
    title: __t('campaign_outfits.column.links'),
    key: 'links',
    width: 350,
    align: 'center',
    render: (col: CampaignOutfit) => {
      if (col.creatorOutfitsShortLink) {
        return (
          <div
            id={getCopyOutfitLinkSelector(col.id)}
            // @ts-ignore
            value={col.creatorOutfitsShortLink}
            className="campaign-outfits__link-wrapper"
          >
            <ActionLink
              link={col.creatorOutfitsShortLink}
              size="inherit"
              color="light"
              className="campaign-outfits__link text-overflow-ellipsis"
              openInNewTab
            >
              {col.creatorOutfitsShortLink}
            </ActionLink>
          </div>
        );
      }

      return (
        <div className="campaign-outfits__link-wrapper">
          {__t('campaign_outfits.link_not_generated')}
        </div>
      );
    }
  },
  {
    title: __t('campaign_outfits.column.dates'),
    key: 'dates',
    align: 'center',
    sort: (a: CampaignOutfit, b: CampaignOutfit) => a.linkCreatedAt?.localeCompare(b.linkCreatedAt),
    render: (col: CampaignOutfit) => {
      const date = col.linkCreatedAt && isToday(col.linkCreatedAt) ? __t('campaign_outfits.today') : null;
      return (
        <div className="campaign-outfits__date">
          { date || getDisplayDate(col.linkCreatedAt) || '—' }
        </div>
      );
    }
  }
];

class CampaignOutfits extends React.PureComponent<Props> {
  componentDidMount() {
    const { campaignId, dispatchFetchCampaignOutfits, dispatchFetchInfluncerMetrics } = this.props;
    dispatchFetchCampaignOutfits(campaignId);
    dispatchFetchInfluncerMetrics(campaignId);
  }

  componentWillUnmount() {
    const { dispatchResetCampaignOutfits } = this.props;
    dispatchResetCampaignOutfits();
  }

  copyLinkToClipboard = (creatorOutfitId: number) => {
    const { dispatchSuccessMessage } = this.props;
    const isCopied = copyTextToClipboard(`#${getCopyOutfitLinkSelector(creatorOutfitId)}`, true);

    if (isCopied) {
      dispatchSuccessMessage(__t('campaign_outfits.link_copied_message'));
    }
  };

  getActionColumn = (campaignId: number): SortableTableColumnType => ({
    title: __t('campaign_outfits.column.actions'),
    key: 'actions',
    display: 'flex',
    align: 'center',
    width: 142,
    render: (col: CampaignOutfit) => (
      <div className="campaign-outfits__actions">
        <Tooltip
          width="short"
          message={__t('campaign_outfits.tooltip.view')}
          classNameWrapper="campaign-outfits__tooltip"
        >
          <SVGDynamicIcon
            name="eye-custom-bg"
            width={24}
            height={24}
            customColor="grey"
            openInNewTab
            disabled={!col.creatorOutfitsShortLink}
            link={col.creatorOutfitsLink}
          />
        </Tooltip>
        <Tooltip
          width="short"
          message={__t('campaign_outfits.tooltip.copy')}
          classNameWrapper="campaign-outfits__tooltip"
        >
          <SVGDynamicIcon
            name="link-custom-bg"
            width={24}
            height={24}
            customColor="grey"
            disabled={!col.creatorOutfitsShortLink}
            onClick={() => this.copyLinkToClipboard(col.id)}
          />
        </Tooltip>
        {
          !isInfluencerProfileRejected(col.creatorProfile) && (
            <Tooltip
              width="short"
              message={__t('campaign_outfits.tooltip.messenger')}
              classNameWrapper="campaign-outfits__tooltip"
            >
              <SVGDynamicIcon
                name="speech-bubble-custom-fg"
                width={24}
                height={24}
                customColor="grey"
                openInNewTab
                link={`/campaigns/${campaignId}/messages?subcampaignId=${col.subcampaignId}`}
              />
            </Tooltip>
          )
        }
      </div>
    )
  });

  addOutfits = (creatorIds: Array<number>) => {
    const { campaignId, dispatchCreateCampaignOutfits } = this.props;
    dispatchCreateCampaignOutfits(campaignId, creatorIds);
  };

  render() {
    const { campaignId, campaignOutfitsItems, campaignOutfitsLoading, influencersMetrics } = this.props;
    const data = campaignOutfitsItems.map(item => ({
      ...item,
      id: item.subcampaignId
    }));

    return (
      <React.Fragment>
        <PageHeading
          color="light"
          margin="none"
          heading={__t('campaign_outfits.title')}
        />
        <CampaignOutfitsHeader campaignOutfitsItems={campaignOutfitsItems} onAddOutfits={this.addOutfits} />
        <PageSection
          style="volume"
          padding="none"
          margin="none"
          customClass="campaign-outfits__section"
        >
          {
            campaignOutfitsLoading ?
              <div className="campaign-outfits__spinner">
                <Spinner
                  position="inline"
                  size="small"
                  color="light"
                />
              </div> :
              <SortableTable
                layout="dense"
                data={data}
                columns={[
                  ...getColumns(influencersMetrics),
                  this.getActionColumn(campaignId)
                ]}
                emptyMessage={__t('campaign_outfits.empty_message')}
                emptyMessageClassName="campaign-outfits__empty-message"
              />
          }
        </PageSection>
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ campaignOutfits, influencersMetrics }): StateProps => ({
  campaignOutfitsItems: campaignOutfits.outfits,
  campaignOutfitsLoading: campaignOutfits.isLoading,
  influencersMetrics: influencersMetrics.data
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchCampaignOutfits: (campaignId) => dispatch(fetchCampaignOutfits(campaignId)),
  dispatchResetCampaignOutfits: () => dispatch(resetCampaignOutfits()),
  dispatchSuccessMessage: (message) => dispatch(success(message, { hideDuration: 2000 })),
  dispatchCreateCampaignOutfits: (campaignId: number, creatorIds: Array<number>) => dispatch(createCampaignOutfits(campaignId, creatorIds)),
  dispatchFetchInfluncerMetrics: (campaignId: number) => dispatch(fetchInfluncerMetrics(campaignId))
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignOutfits);
